/**
 * Game interface 계약 — Plan 08-02 Task 1 (GAME-01).
 *
 * 08-03 의 3 게임 (룰렛·슬롯·경주) 이 공유하는 계약.
 * engine.registerGame(game) 시 game.init(ctx) 호출 후 engine 이 이벤트 relay.
 *
 * L4 compliance (DESIGN §21):
 *   - meta.probabilityDisclosure 필수 문구 (확률 공시).
 *   - GameResult 는 단일 라운드 결과만 보존 — 누적 랭킹 금지.
 *   - GameMeta.prizes 의 구체 schema 는 @loop/contracts game.rest (Plan 08-02 Task 2 / Plan 08-03).
 */
import type { Application, Container } from 'pixi.js';

/** 내부 3종 게임. Wave 2 외부 플러그인은 GameKind 에 포함되지 않음 (별도 PluginKind). */
export type GameKind = 'roulette' | 'slot' | 'race';

/**
 * Game metadata — 08-03 구현시 각 game 이 정적 제공.
 * prizes 는 game-specific 이므로 unknown[] (@loop/contracts 에서 구체 schema).
 */
export interface GameMeta {
  readonly kind: GameKind;
  readonly name: string;
  readonly version: string;
  readonly prizes: readonly unknown[];
  /** DESIGN §21.5 확률 공시 — 약관/안내 페이지 노출 텍스트. */
  readonly probabilityDisclosure: string;
}

/** 단일 라운드 결과. 누적 랭킹 금지 → 어떤 형태로든 다수 라운드 집계 금지. */
export interface GameResult {
  readonly kind: GameKind;
  readonly winner?: unknown;
  /** 해당 결과를 트리거한 donation eventId — idempotency 추적용. */
  readonly donationEventId: string;
  /** ISO8601 timestamp. */
  readonly at: string;
}

/**
 * Engine 이 Game 으로 전달하는 donation event — Wave 0 DonationToast 를 간소화.
 * Wave 0 WS `donation.toast` (@loop/contracts/ws) 에서 mapping.
 */
export interface DonationEvent {
  /** 멱등 dedupe key — Wave 0 donationId uuid. */
  readonly eventId: string;
  readonly streamerId?: string;
  readonly fanId?: string;
  /** KRW 정수. */
  readonly amount: number;
  readonly nickname: string;
  readonly message: string;
  /** ISO8601. */
  readonly at: string;
}

/**
 * Engine → Game 호출 context.
 * - stage 는 game 이 render 대상 container 로 사용.
 * - reducedMotion=true → game 은 애니메이션 skip (즉시 result 표출).
 * - reportResult 는 game 이 라운드 끝날 때 호출.
 * - config 는 game-specific (Zod validated upstream by 08-03).
 */
export interface GameContext {
  readonly stage: Container;
  readonly app: Application;
  readonly reducedMotion: boolean;
  reportResult(result: GameResult): void;
  readonly config: unknown;
}

/** 3 게임이 구현해야 하는 최소 계약. */
export interface Game {
  readonly meta: GameMeta;
  init(ctx: GameContext): Promise<void>;
  onDonation(event: DonationEvent): Promise<void>;
  destroy(): void;
}

/**
 * Game meta 최소 검증 — Engine.registerGame 진입점에서 사용.
 * 08-03 구현이 필드를 누락해도 runtime 에서 즉시 탐지.
 */
export function assertGameMeta(meta: unknown): asserts meta is GameMeta {
  if (!meta || typeof meta !== 'object') {
    throw new TypeError('Game.meta is missing or not an object');
  }
  const m = meta as Record<string, unknown>;
  if (m.kind !== 'roulette' && m.kind !== 'slot' && m.kind !== 'race') {
    throw new TypeError(`Game.meta.kind invalid: ${String(m.kind)}`);
  }
  if (typeof m.name !== 'string' || m.name.length === 0) {
    throw new TypeError('Game.meta.name missing');
  }
  if (typeof m.version !== 'string' || m.version.length === 0) {
    throw new TypeError('Game.meta.version missing');
  }
  if (!Array.isArray(m.prizes)) {
    throw new TypeError('Game.meta.prizes must be array');
  }
  if (typeof m.probabilityDisclosure !== 'string' || m.probabilityDisclosure.length === 0) {
    throw new TypeError('Game.meta.probabilityDisclosure missing — L4 compliance');
  }
}
